<template>
  <page-main :title="pageTitle" noBottom :error="error">
    <template v-slot:aside>
      Review employee's resume.
      <ul>
        <li>Select different resume templates.</li>
        <li>Download in PDF or Word format.</li>
        <li>Download complete profile data (JSON).</li>
      </ul>
    </template>
    <v-container style="position: relative">
      <v-overlay :absolute="true" v-if="overlay">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-overlay>
      <v-row class="pb-3" justify="center">
        <v-col cols="12" sm="5" md="4" lg="4">
          <div>
            <p class="text-h6 mb-2">{{ resumeTitle }}</p>
            <p class="text-body-2 text--secondary font-italic mb-0">
              {{ resumeDesc }}
            </p>
          </div>
        </v-col>
        <v-col cols="12" sm="5" md="5" lg="4">
          <resume-selector
            :type.sync="resumeType"
            @update:title="updateTitle($event)"
            @update:desc="updateDesc($event)"
          ></resume-selector>
          <v-btn
            block
            outlined
            @click="downloadPdf()"
            id="download-pdf"
            class="mt-3"
            >Download PDF</v-btn
          >
          <v-btn
            block
            outlined
            @click="downloadWord()"
            id="download-word"
            class="mt-3"
            >Download Word (.docx)</v-btn
          >
          <v-btn
            block
            outlined
            @click="downloadFullProfile()"
            id="download-profile"
            class="mt-3"
            >Download Full Profile</v-btn
          >
          <v-btn block outlined @click="goBack()" id="back-button" class="mt-3">
            Back to Employee List
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="d-flex justify-center">
          <div v-if="errorOnLoad">
            <div
              class="d-flex flex-column justify-center align-center error-box"
            >
              <div class="pb-5 error-box-content">
                <h1>Ouch!</h1>
                <h2>We couldn't load this resume!</h2>
              </div>
              <div class="error-box-content">
                <div class="font-italic pb-2">
                  We're looking into the problem - in the meantime you can try
                  reloading the page:
                </div>
                <v-btn @click="refresh()" outlined>Refresh</v-btn>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="resume-backing pa-3 mb-4 pb-1">
              <img
                v-if="resumeImageSrc.length > 0"
                :src="resumeImageSrc"
                class="resume-img"
                alt="Employee Resume View"
              />
              <div v-else-if="useHtmlPreview" class="html-resume-preview" ref="htmlPreview" v-html="buildResumeHtml()"></div>
              <div v-else class="resume-placeholder"></div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </page-main>
</template>

<script>
import * as log from "../../utility/logging/logger";
import { mapGetters } from "vuex";
import { mapFields } from "vuex-map-fields";
import * as companyApi from "../../api/CompanyApi";
import DataAccess from "../../report/DataAccess.js";
import * as ReportAPI from "../../api/ReportApi.js";
import ReportBuilder from "../../reportBuilder/ReportBuilder";
import ReportBuilderBase from "../../reportBuilder/ReportBuilderBase";
import ResumeSelector from "../../components/ResumeSelector.vue";
import {
  cleanProfileData,
  downloadProfileAsJson,
} from "../../utility/profileExport";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

export default {
  name: "EmployeeResume",
  components: {
    "resume-selector": ResumeSelector,
  },
  data: () => ({
    customizations: {
      professionalSummary: { checked: true },
      experience: { checked: true, workList: [] },
      education: { checked: true, educationList: [] },
      skills: { checked: true, skillList: [] },
      additionalInfo: {
        checked: true,
        additionalInfoList: [
          { checked: true, label: ReportBuilderBase.softSkillsLabels },
          { checked: true, label: ReportBuilderBase.rolesLabel },
          { checked: true, label: ReportBuilderBase.languagesLabel },
          { checked: true, label: ReportBuilderBase.licensesLabel },
          { checked: true, label: ReportBuilderBase.profOrgLabel },
          { checked: true, label: ReportBuilderBase.volunteerOrgLabel },
          { checked: true, label: ReportBuilderBase.interestsLabel },
          { checked: true, label: ReportBuilderBase.otherLabel },
        ],
      },
    },
    employee: {
      id: "",
      name: "",
      email: "",
    },
    overlay: true,
    errorOnLoad: false,
    error: "",
    resumeType: "Professional",
    imgData: "",
    resumeTitle: "",
    resumeDesc: "",
    token: "",
    dateExpires: {},
    builder: {},
    reportRequest: {},
    rawResumeData: null,
    useHtmlPreview: false,
  }),
  computed: {
    ...mapFields("name", { nameDisplay: "displayValue" }),
    ...mapFields("address", { addressDisplay: "displayValue" }),
    ...mapFields("hardSkill", ["hardSkills", "hardSkillLabels"]),
    ...mapFields("softSkill", ["softSkills"]),
    ...mapFields("licenseCert", ["licensesCertsDisplay"]),
    ...mapFields("adjective", ["adjectiveDisplay"]),
    ...mapFields("role", ["selectedRoles"]),
    ...mapFields("language", ["languagesDisplay"]),
    ...mapFields("organization", [
      "volunteerOrgDisplay",
      "professionalOrgDisplay",
    ]),
    ...mapFields("personalInterest", ["personalInterestsDisplay"]),
    ...mapFields("careerInterest", ["careerInterestsDisplay"]),
    ...mapFields("work", ["workDict"]),
    ...mapFields("education", ["educationDict"]),
    ...mapFields("finalInfo", ["otherSkills", "points", "professionalSummary"]),
    ...mapGetters("education", { eduList: "fullList" }),
    ...mapGetters("work", { workList: "fullList" }),
    employeeId() {
      return this.$route.params.id;
    },
    pageTitle() {
      return this.employee.name
        ? `${this.employee.name}'s Resume`
        : "Employee Resume";
    },
    resumeImageSrc() {
      if (this.imgData.length === 0) {
        return "";
      } else {
        return this.imgData;
      }
    },
  },
  async mounted() {
    this.dateExpires = new Date(new Date().getTime() + -60 * 1000);
    await this.loadEmployeeResume();
  },
  watch: {
    resumeType: {
      async handler() {
        await this.initReportBuilder(this.resumeType);
      },
    },
  },
  methods: {
    updateDesc(desc) {
      this.resumeDesc = desc;
    },
    updateTitle(title) {
      this.resumeTitle = title;
    },
    async loadEmployeeResume() {
      try {
        this.overlay = true;
        this.errorOnLoad = false;
        this.error = "";
        // TODO, maybe not call GetAll here as might be inefficient
        const employeesResult = await companyApi.GetAllEmployees();
        if (!employeesResult.success) {
          this.errorOnLoad = true;
          this.error = "Failed to load employee data.";
          this.overlay = false;
          return;
        }
        const employee = employeesResult.data?.employees?.find(
          (emp) => emp.id === this.employeeId
        );

        if (!employee || !employee.latestResumeId) {
          this.errorOnLoad = true;
          this.error = "No resume found for this employee.";
          this.overlay = false;
          return;
        }

        const result = await companyApi.GetEmployeeResume(
          this.employeeId,
          employee.latestResumeId
        );

        if (!result.success || !result.data) {
          this.errorOnLoad = true;
          this.error = result.error || "Resume data is empty or invalid.";
          this.overlay = false;
          return;
        }

        const rd = result.data;
        this.rawResumeData = rd;
        this.employee.id = this.employeeId;

        let userName = rd.ResName?.displayValue || "";
        if (!userName && rd.ResName) {
          const parts = [
            rd.ResName.firstName,
            rd.ResName.middleName,
            rd.ResName.lastName,
            rd.ResName.suffix,
          ].filter(Boolean);
          userName = parts.join(" ").trim();
        }
        if (!userName && rd.Address?.email) {
          userName = rd.Address.email.split("@")[0];
        }
        if (!userName && rd.userId) {
          userName = rd.userId.split("@")[0];
        }

        this.employee.name =
          userName || employee.name || employee.email || "Employee";
        this.employee.email = rd.Address?.email || employee.email || "";

        this.$store.commit("name/loadData", rd.ResName || { displayValue: "" });
        this.$store.commit(
          "address/loadData",
          rd.Address || { displayValue: "" }
        );
        this.$store.commit(
          "hardSkill/loadData",
          rd.HardSkill || { HardSkills: [], HardSkillLabels: [] }
        );
        this.$store.commit(
          "softSkill/loadData",
          rd.SoftSkill || { SoftSkills: [] }
        );
        this.$store.commit(
          "licenseCert/loadData",
          rd.LicensesCert || { LicensesCertsDisplay: "" }
        );
        this.$store.commit(
          "adjective/loadData",
          rd.SelectedAdjectives || { AdjectiveDisplay: "" }
        );
        this.$store.commit("role/loadData", rd.SelectedRoles || { Roles: [] });
        this.$store.commit(
          "language/loadData",
          rd.Language || { LanguagesDisplay: "" }
        );
        this.$store.commit("organization/loadData", {
          ProfessionalOrgDisplay:
            rd.ProfessionalOrganization?.ProfessionalOrgDisplay || "",
          VolunteerOrgDisplay:
            rd.VolunteerOrganizations?.VolunteerOrgDisplay || "",
        });
        this.$store.commit(
          "personalInterest/loadData",
          rd.PersonalInterest || { PersonalInterestsDisplay: "" }
        );
        this.$store.commit(
          "careerInterest/loadData",
          rd.CareerInterest || { CareerInterestsDisplay: "" }
        );
        this.$store.commit("work/loadData", rd.Work || { WorkDict: {} });
        this.$store.commit(
          "education/loadData",
          rd.Education || { EducationDict: {} }
        );
        this.$store.commit(
          "finalInfo/loadData",
          rd.FinalInfo || {
            otherSkills: [],
            points: [],
            professionalSummary: "",
          }
        );

        await this.$nextTick();

        this.setupCustomizations();
        await this.initReportBuilder(this.resumeType);
      } catch (error) {
        log.logError(
          "Error loading employee resume",
          "EmployeeResume.loadEmployeeResume"
        );
        this.errorOnLoad = true;
        this.error = "Failed to load employee resume. Please try again.";
        this.overlay = false;
      }
    },
    async initReportBuilder(resumeType) {
      try {
        this.overlay = true;
        this.errorOnLoad = false;

        this.buildReportRequest(resumeType);

        const reportURL = this.$store.state.settings?.reportURL || "";
        if (reportURL && reportURL.length > 0) {
          let da = new DataAccess(reportURL);
          let token = await this.getToken();
          if (token !== null) {
            try {
              let image = await da.GetReportPNG(this.reportRequest, token);
              if (image) {
                this.imgData = URL.createObjectURL(image);
                this.overlay = false;
                return;
              }
            } catch (e) {
              log.logWarning("Report server unavailable, using HTML preview", "EmployeeResume.initReportBuilder");
            }
          }
        }

        this.useHtmlPreview = true;
        this.overlay = false;
      } catch (error) {
        log.logError(
          "Unhandled error in initReportBuilder code",
          "EmployeeResume.initReportBuilder"
        );
        log.logError(`${error.name}: ${error.message}`);
        this.errorOnLoad = true;
        this.overlay = false;
      }
    },
    buildReportRequest(resumeType) {
      this.builder = new ReportBuilder(resumeType, this.customizations);

      this.reportRequest = this.builder.BuildReport(
        this.nameDisplay,
        this.addressDisplay,
        this.adjectiveDisplay,
        this.hardSkills,
        this.hardSkillLabels,
        this.softSkills,
        this.selectedRoles,
        this.careerInterestsDisplay,
        this.eduList,
        this.workList,
        this.workDict,
        this.languagesDisplay,
        this.licensesCertsDisplay,
        this.professionalOrgDisplay,
        this.volunteerOrgDisplay,
        this.personalInterestsDisplay,
        this.otherSkills,
        this.points,
        this.professionalSummary
      );
    },
    async getToken() {
      if (new Date() > this.dateExpires) {
        var tokenResult = await ReportAPI.GetReportToken();
        if (tokenResult.success) {
          this.token = tokenResult.data.access_token;
          this.dateExpires = new Date(
            new Date().getTime() + tokenResult.data.expires_in * 1000
          );
          return this.token;
        } else {
          return null;
        }
      } else {
        return this.token;
      }
    },
    async downloadPdf() {
      try {
        this.overlay = true;
        const reportURL = this.$store.state.settings?.reportURL || "";
        
        if (reportURL && reportURL.length > 0) {
          let da = new DataAccess(reportURL);
          var token = await this.getToken();
          if (token !== null) {
            let report = await da.GetReportPDF(this.reportRequest, token);
            if (report) {
              this.downloadFile(report, `${this.employee.name} Resume.pdf`);
              this.overlay = false;
              return;
            }
          }
        }

        await this.downloadPdfClientSide();
        this.overlay = false;
      } catch (error) {
        log.logError(
          "Unhandled error while downloading pdf",
          "EmployeeResume.downloadPdf"
        );
        this.error =
          "Ouch! We had an unexpected error while downloading the resume.";
        this.overlay = false;
      }
    },
    async downloadPdfClientSide() {
      const resumeHtml = this.buildResumeHtml();
      
      const container = document.createElement('div');
      container.innerHTML = resumeHtml;
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = '8.5in';
      container.style.padding = '0.5in';
      container.style.backgroundColor = 'white';
      container.style.fontFamily = 'Arial, sans-serif';
      document.body.appendChild(container);

      try {
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });
        
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'in', 'letter');
        const imgWidth = 8.5;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        let heightLeft = imgHeight;
        let position = 0;
        const pageHeight = 11;
        
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        
        pdf.save(`${this.employee.name} Resume.pdf`);
      } finally {
        document.body.removeChild(container);
      }
    },
    buildResumeHtml() {
      const sections = [];
      
      sections.push(`<div style="text-align: center; margin-bottom: 20px;">
        <h1 style="margin: 0; font-size: 24px; color: #333;">${this.nameDisplay || ''}</h1>
        <p style="margin: 5px 0; color: #666; font-size: 12px;">${this.addressDisplay || ''}</p>
      </div>`);
      
      if (this.customizations.professionalSummary.checked) {
        const summary = this.builder?.BuildProfessionalSummary?.(
          this.adjectiveDisplay, this.careerInterestsDisplay, 
          this.selectedRoles?.join(', ') || '', 
          this.hardSkills?.slice(0, 5).join(', ') || '',
          this.softSkills?.join(', ') || '', this.workDict
        ) || this.professionalSummary || '';
        if (summary) {
          sections.push(`<div style="margin-bottom: 15px;">
            <h2 style="font-size: 14px; border-bottom: 2px solid #084492; padding-bottom: 5px; color: #084492;">Professional Summary</h2>
            <p style="font-size: 11px; line-height: 1.5; color: #333;">${summary}</p>
          </div>`);
        }
      }
      
      if (this.customizations.experience.checked && this.workList?.length > 0) {
        let workHtml = `<div style="margin-bottom: 15px;">
          <h2 style="font-size: 14px; border-bottom: 2px solid #084492; padding-bottom: 5px; color: #084492;">Experience</h2>`;
        
        this.workList.forEach(work => {
          const wg = work.workGeneral || {};
          const startDate = wg.startDate ? new Date(wg.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
          const endDate = wg.workingCurrently ? 'Present' : (wg.endDate ? new Date(wg.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '');
          
          workHtml += `<div style="margin-bottom: 12px;">
            <div style="display: flex; justify-content: space-between;">
              <strong style="font-size: 12px;">${wg.jobTitle || ''}</strong>
              <span style="font-size: 11px; color: #666;">${startDate} - ${endDate}</span>
            </div>
            <div style="font-size: 11px; color: #666; font-style: italic;">${wg.companyName || ''}</div>`;
          
          if (work.responsibilities?.length > 0) {
            workHtml += `<ul style="margin: 5px 0; padding-left: 20px; font-size: 11px;">`;
            work.responsibilities.forEach(resp => {
              if (resp?.value) {
                workHtml += `<li style="margin-bottom: 3px;">${resp.value}</li>`;
              }
            });
            workHtml += `</ul>`;
          }
          workHtml += `</div>`;
        });
        workHtml += `</div>`;
        sections.push(workHtml);
      }
      
      if (this.customizations.education.checked && this.eduList?.length > 0) {
        let eduHtml = `<div style="margin-bottom: 15px;">
          <h2 style="font-size: 14px; border-bottom: 2px solid #084492; padding-bottom: 5px; color: #084492;">Education</h2>`;
        
        this.eduList.forEach(edu => {
          eduHtml += `<div style="margin-bottom: 8px;">
            <strong style="font-size: 12px;">${edu.degree || ''} ${edu.major ? 'in ' + edu.major : ''}</strong>
            <div style="font-size: 11px; color: #666;">${edu.school || ''}</div>
          </div>`;
        });
        eduHtml += `</div>`;
        sections.push(eduHtml);
      }
      
      if (this.customizations.skills.checked && this.hardSkills?.length > 0) {
        sections.push(`<div style="margin-bottom: 15px;">
          <h2 style="font-size: 14px; border-bottom: 2px solid #084492; padding-bottom: 5px; color: #084492;">Skills</h2>
          <p style="font-size: 11px; line-height: 1.5;">${this.hardSkills.join(', ')}</p>
        </div>`);
      }
      
      return `<div style="font-family: Arial, sans-serif; color: #333;">${sections.join('')}</div>`;
    },
    async downloadWord() {
      try {
        let report = await this.buildDocx();
        this.downloadFile(report, `${this.employee.name} Resume.docx`);
      } catch (error) {
        log.logError(
          "Unhandled error while downloading docx",
          "EmployeeResume.downloadWord"
        );
        this.error =
          "Ouch! We had an unexpected error while downloading the resume.";
      }
    },
    async buildDocx() {
      return await this.builder.BuildDocx(
        this.nameDisplay,
        this.addressDisplay,
        this.adjectiveDisplay,
        this.hardSkills,
        this.hardSkillLabels,
        this.softSkills,
        this.selectedRoles,
        this.careerInterestsDisplay,
        this.eduList,
        this.workList,
        this.workDict,
        this.languagesDisplay,
        this.licensesCertsDisplay,
        this.professionalOrgDisplay,
        this.volunteerOrgDisplay,
        this.personalInterestsDisplay,
        this.otherSkills,
        this.points,
        this.professionalSummary
      );
    },
    async downloadFullProfile() {
      try {
        if (!this.rawResumeData) {
          this.error = "No resume data available. Cannot export profile.";
          return;
        }

        const cleanedData = cleanProfileData(this.rawResumeData);

        if (!cleanedData || !cleanedData.ResName) {
          this.error = "Failed to process resume data. Cannot export profile.";
          return;
        }

        downloadProfileAsJson(cleanedData, this.employee.name || "Employee");
      } catch (error) {
        log.logError(
          "Error downloading employee profile",
          "EmployeeResume.downloadFullProfile"
        );
        log.logError(`${error.name}: ${error.message}`);
        this.error = "Failed to download profile. Please try again.";
      }
    },
    setupCustomizations() {
      this.customizations.experience.workList = [];
      this.customizations.education.educationList = [];
      this.customizations.skills.skillList = [];

      this.setupWorkCustomizations();
      this.setupEducationCustomizations();
      this.setupSkillsCustomizations();
    },
    setupSkillsCustomizations() {
      if (Array.isArray(this.hardSkillLabels)) {
        this.hardSkillLabels.forEach((hsl) => {
          this.customizations.skills.skillList.push({
            checked: true,
            level: hsl.level,
            label: hsl.text,
          });
        });
      }
      this.customizations.skills.skillList.push({
        checked: true,
        level: "other",
        label: "Other Skills",
      });
    },
    setupEducationCustomizations() {
      if (Array.isArray(this.eduList)) {
        this.eduList.forEach((eduElement) => {
          let educationItem = {
            checked: true,
            key: eduElement.key,
            label: eduElement.general,
            accolades: Array.isArray(eduElement.accolades)
              ? eduElement.accolades.map((accolade) => {
                  return { checked: true, label: accolade };
                })
              : [],
          };
          if (eduElement.gpa) {
            educationItem = {
              ...educationItem,
              gpa: { label: eduElement.gpa, checked: true },
            };
          }
          this.customizations.education.educationList.push(educationItem);
        });
      }
    },
    setupWorkCustomizations() {
      if (Array.isArray(this.workList)) {
        this.workList.forEach((workElement) => {
          const label = `${workElement.general}: ${
            Array.isArray(workElement.roles) ? workElement.roles.join("; ") : ""
          }`;
          this.customizations.experience.workList.push({
            checked: true,
            label: label,
            key: workElement.key,
            responsibilities: {
              checked: true,
              label: workElement.responsibilities || "",
            },
            achievements: {
              checked: true,
              list: Array.isArray(workElement.achievements)
                ? workElement.achievements.map((e) => {
                    return { checked: true, label: e };
                  })
                : [],
            },
          });
        });
      }
    },
    downloadFile(blob, fileName) {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 7000);
    },
    refresh() {
      log.logWarning(
        "User triggered refresh of Employee Resume page due to error.",
        "EmployeeResume.refresh"
      );
      window.location.reload();
    },
    goBack() {
      this.$router.push({ path: "/Company/EmployeeList" });
    },
  },
};
</script>

<style scoped>
.error-box {
  width: 636px;
  height: 816px;
  background-color: var(--v-resumeErrorBox-base);
  color: var(--v-resumeErrorText-base);
}

.error-box-content {
  width: 70%;
}

.resume-placeholder {
  width: 612px;
  height: 792px;
  background-color: var(--v-resumePlaceholder-base);
}

.resume-backing {
  background-color: var(--v-resumeBacker-base);
  max-width: 636px;
}

.resume-img {
  max-width: 100%;
  height: auto;
}

.html-resume-preview {
  width: 612px;
  min-height: 792px;
  background-color: white;
  padding: 0.5in;
  font-family: Arial, sans-serif;
  font-size: 11px;
  line-height: 1.4;
  color: #333;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
</style>
